/* eslint-disable max-nested-callbacks */
import MessageCard from 'components/card/MessageCard'
import PluviometryAction from 'pluviometry/actions/PluviometryAction'
import PluviometerDto from 'pluviometry/dto/PluviometerDto'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import { orderBy, uniqBy, minBy, min, isUndefined } from 'lodash'
import { getBeginingOfTheYear, getDate, getEndOfTheYear } from 'utils/DateUtil'
import { exportFile } from 'utils/ExportDataUtil'
import { hasValue } from 'utils/NumberUtil'
import { PluvioCumulOptionIcon } from 'catchment/components/catchment/dashboard/pluviometry/PluvioOption'
import useApplicationSetting, { intParser } from 'utils/customHook/useApplicationSetting'
import { Card, CardContent, Grid } from '@mui/material'
import useStartPeriod from 'catchment/utils/useStartPeriod'
import useEndPeriod from 'catchment/utils/useEndPeriod'
import { CardTitle } from 'components/card/NewCard'
import PluvioChart from './PluvioChart'
import useLocalStorage from 'utils/customHook/useLocalStorage'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'
import CircularProgressWithLabel from 'components/progress/CircularProgressWithLabel'
import { getSettingBool } from 'utils/SettingUtils'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import { SHOW_INSEE_CODE } from 'account/constants/AccountSettingsConstants'

const EXPORT_ALL_HEADERS = ['code', 'name', 'cityCode', 'cityLabel', 'dataType', 'date', 'value']
const EXPORT_NO_INSEE_CODE = ['code', 'name', 'cityLabel', 'dataType', 'date', 'value']

const barTypes = [-1, 1, 3]

const CatchmentFollowUpPluvio = ({
    pluviometersLinked = [],
}) => {
    const dispatch = useDispatch()

    const {
        catchment,
        pluviometers,
        pluviometryDataTypes,
        cities,
        settings,
    } = useSelector(store => ({
        catchment: store.CatchmentReducer.catchment,
        pluviometers: store.PluviometryReducer.pluviometers,
        pluviometryDataTypes: store.PluviometryReducer.pluviometryDataTypes,
        cities: store.CityReducer.cities,
        settings: store.AdministrationReducer.applicationSettings,
    }), shallowEqual)

    const startPeriod = useStartPeriod(intParser)
    const endPeriod = useEndPeriod(intParser)

    const startDate = getBeginingOfTheYear(startPeriod)
    const endDate = getEndOfTheYear(endPeriod)

    const [dataTypes, setDataTypes] = useLocalStorage('CATCHMENT_PLUVIO_DATATYPES', [1]) // pluie
    const [dataMeasures, setDataMeasures] = useState([])
    const [group, setGroup] = useLocalStorage('CATCHMENT_PLUVIO_GROUP', 'SUM_AUTO')

    const filteredDataTypes = useMemo(() => pluviometryDataTypes.filter(dt => dt.showData || dataTypes.includes(dt.id)), [dataTypes, pluviometryDataTypes])

    const getDefaultSelectPointId = () => {
        const mainPointFound = catchment.catchmentPoints?.find(({ mainPoint }) => mainPoint)
        const code = mainPointFound?.codeWithoutDesignation
        const idMainPoint = mainPointFound && pluviometers.find(q => q.code === code)?.id
        if (hasValue(idMainPoint) && dataMeasures.find(data => data.stationId === idMainPoint)) {
            return [idMainPoint]
        }
        return dataMeasures.length ? [parseInt(dataMeasures[0]?.stationId)] : []
    }

    const [stations, setStations] = useState(getDefaultSelectPointId)

    useEffect(() => {
        setStations(getDefaultSelectPointId)
    }, [catchment, pluviometers, dataMeasures])

    useEffect(() => {
        dispatch(PluviometryAction.fetchPluviometryDataTypes())
    }, [dispatch])

    const {
        isLoaded,
        progress,
    } = useProgressDispatch((cancelRef) => {
        setDataMeasures(prev => prev.filter(dm => dataTypes.includes(dm.dataType)))
        const listInputs = pluviometersLinked.flatMap(p => {
            return dataTypes
                .filter(dt => !dataMeasures.some(dm => dm.stationId === p.id && dm.dataType === dt && dm.groupFunc === group))
                .map(dataType => ({
                    groupFunc: group.startsWith('CUMUL') && group || barTypes.includes(dataType) && group || 'MAX',
                    chartMode: true,
                    startDate: getBeginingOfTheYear(startPeriod),
                    endDate: getEndOfTheYear(endPeriod),
                    dataType,
                    stationId: p.id,
                }))
        })

        return listInputs.map(input => dispatch(PluviometryAction.getPluvioChronicMeasures(input))
            .then(data => !cancelRef.current && setDataMeasures(prev => uniqBy([data, ...prev], d => `${d.stationId}#${d.dataType}`))))
    }, [group, dataTypes])

    const dataMeasuresFiltered = useMemo(() => {
        const orderedDataMeasures = orderBy(dataMeasures, 'dataType')
        if (stations.length === 0) return orderedDataMeasures
        return orderedDataMeasures.filter(({ stationId }) => stations.includes(stationId))
    }, [dataMeasures, stations])

    const reductFirstYear = (measuresToCheck) => {
        const minByCurve = measuresToCheck.map(m => m.measures ? m.measures : []).map(m => m.length > 0 ? minBy(m, 'date').date : undefined)
        const minForAllCurves = isUndefined(min(minByCurve)) ? startDate : min(minByCurve)
        return getBeginingOfTheYear(getDate(minForAllCurves, 'YYYY'))
    }

    const firstCatchmentYear = getSettingBool(settings, 'FIRST_CATCHMENT_YEAR')
    const newStartDate = (firstCatchmentYear && dataMeasures.length > 0) ? reductFirstYear(dataMeasures) : startDate

    const generalShowInseeCode = useApplicationSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : true)
    const showInseeCode = useAccountSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : generalShowInseeCode)

    const actions = [
        <PluvioCumulOptionIcon
            key={0}
            defaultGroupType={group}
            onChangeGroupType={setGroup}
            disabled={!isLoaded}
        />,
        {
            icon: 'file_download',
            tooltip: i18n.export,
            onClick: () => {
                const exportData = dataMeasures.flatMap(dm => {
                    const pluvio = pluviometers.find(({ id }) => id === dm.stationId)
                    const dataType = pluviometryDataTypes.find(d => d.id === dm.dataType)
                    const city = pluvio && cities.find(c => c.code === pluvio?.townCode)
                    return dm.measures.map(m => ({
                        code: pluvio?.code,
                        name: pluvio?.name || dm.stationId,
                        cityCode: city?.code,
                        cityLabel: city?.name,
                        dataType: dataType?.label ?? '',
                        date: { value: getDate(m.date), format: 'dd/MM/yyyy', cellType: 'date' },
                        value: { value: m.value, format: '0.00000', cellType: 'number' },
                    }))
                })
                exportFile({
                    data: exportData.length ? [
                        {
                            ...exportData[0],
                            headers: showInseeCode ? EXPORT_ALL_HEADERS : EXPORT_NO_INSEE_CODE,
                        },
                        ...exportData.slice(1),
                    ] : [],
                    exportType: 'xlsx',
                    titleFile: i18n.dataPluvio,
                }, true)
            },
        },
    ]

    const formattedPluviometers = useMemo(() => {
        return pluviometersLinked.map(pluvio => {
            const city = pluvio && cities.find(c => c.code === pluvio.townCode)
            return {
                name: [pluvio.code ? `[${pluvio.code}]` : '', `${pluvio.name || ''}`, `${city?.name ? `- ${city.name}` : ''}`].join(' '),
                id: pluvio.id,
            }
        })
    }, [cities, pluviometersLinked])

    return (
        <Card data-cy='follow_up_pluvio'>
            <CardTitle title={i18n.pluviometry} actions={actions} />
            <CardContent>
                <Grid container columnSpacing={2} alignItems='center'>
                    <Grid size={6}>
                        <SuperMultiAutocomplete
                            label={i18n.pluviometer}
                            options={formattedPluviometers}
                            values={stations}
                            onChange={setStations}
                            keyValue={'id'}
                            multiple
                            limit={2}
                        />
                    </Grid>
                    <Grid size={4}>
                        <SuperMultiAutocomplete
                            label={i18n.dataTypes}
                            options={filteredDataTypes}
                            onChange={setDataTypes}
                            values={dataTypes}
                            keyValue='id'
                            multiple
                            limit={3}
                        />
                    </Grid>
                </Grid>
                <div style={{ position: 'relative' }}>
                    {!isLoaded && (<CircularProgressWithLabel value={progress} />) }
                    {dataMeasuresFiltered.length > 0 && (
                        <PluvioChart
                            dataMeasures={dataMeasuresFiltered}
                            startDate={newStartDate}
                            endDate={endDate}
                        />
                    )}
                    {dataMeasuresFiltered.length === 0 && (<MessageCard>{i18n.noDataToDisplay}</MessageCard>)}
                </div>
            </CardContent>
        </Card>
    )
}

CatchmentFollowUpPluvio.propTypes = {
    pluviometersLinked: PropTypes.arrayOf(PropTypes.instanceOf(PluviometerDto)).isRequired,
}

export default CatchmentFollowUpPluvio